import type { MutableRefObject } from 'react'
import { useCallback, useRef, useEffect, useImperativeHandle, SetStateAction } from 'react'
import type { TablePaginationConfig } from 'antd/lib/table'
import type {
  PaginatedResult,
  Mutate,
  PaginatedFormatReturn,
} from '@ahooksjs/use-request/lib/types'

import type { RequestService } from '@/utils/hooks/useFetch'
import useFetch from '@/utils/hooks/useFetch'
import type { FieldValues, UseFormReturn, UnpackNestedValue, DeepPartial } from 'react-hook-form'
import { useForm } from 'react-hook-form'

interface IPaginatedResult<Result> {
  current_page: number
  list: Result[]
  num: number
  total_num: number
  total_page: number
}

interface IUseTableSearchReturn<Result, FormField extends FieldValues = FieldValues>
  extends PaginatedResult<Result> {
  form: UseFormReturn<FormField>
  tableRef: MutableRefObject<ITableRef>
}

interface IRequestConfig {
  request: (args: any) => RequestService
  dep: any[]
}

export interface IUseTableSearchConfig<FormField extends FieldValues = FieldValues, Result = any> {
  request: IRequestConfig | IRequestConfig['request']
  defaultValues?: UnpackNestedValue<DeepPartial<FormField>>
  paramsAdapter?<T = any>(formValues: FormField | T): { [k: string]: any }
  onRequestSuccess?: (res: Result, params: any) => void
  onFormat?: (res: BasicResponse<Result> | BasicResponse<IPaginatedResult<Result>>) => Result
  paginated?: boolean
  pageSize?: number
}
interface PageQuery {
  current?: number
  pageSize?: number
}

export interface ITableRef<FormField extends FieldValues = FieldValues> {
  search(pageInfo?: PageQuery): Promise<FormField>
  reset(values?: FormField): Promise<any>
  mutate: Mutate<PaginatedFormatReturn<any>>
  form: UseFormReturn<FormField>
  getRealTimeParams: () => FormField & { num?: number; page?: number }
  getLatestRequestParams: () => (FormField & { num?: number; page?: number }) | undefined
}

const AHOOKS_USE_REQUEST_DEFAULT_KEY = 'AHOOKS_USE_REQUEST_DEFAULT_KEY'

function useTableSearch<
  Result extends BasicResponse<any> = BasicResponse<any>,
  FormField extends FieldValues = FieldValues,
>(config: IUseTableSearchConfig<FormField>): IUseTableSearchReturn<Result, FormField> {
  const { request, paramsAdapter, defaultValues, paginated, onRequestSuccess, pageSize, onFormat } =
    config

  const lastRequestParams = useRef<FormField & { num?: number; total?: number }>({} as any)

  const tableRef = useRef<ITableRef>({} as ITableRef)
  const form = useForm<FormField>({
    defaultValues,
  })

  const defaultFormatter = useCallback((res) => res.data, [])

  const paginatedFormatter = useCallback((res: BasicResponse<IPaginatedResult<Result>>) => {
    return {
      ...res.data,
      total: res.data?.total_num || 0,
    }
  }, [])

  const requestMaterial = useFetch(
    (pageInfo) => {
      const values = form.getValues() || {}
      const params = {
        ...(paramsAdapter?.(values as any) || values),
        page: pageInfo?.current,
        num: pageInfo?.pageSize,
      }
      lastRequestParams.current = params as any
      if (typeof request === 'function') {
        return request(params)
      }
      const req = request.request
      return req(params)
    },
    {
      paginated: paginated as true,
      manual: true,
      formatResult(res) {
        if (typeof onFormat === 'function') {
          return onFormat(res)
        }
        return paginated ? paginatedFormatter(res) : defaultFormatter(res)
      },
      onSuccess(...args) {
        // eslint-disable-next-line prefer-destructuring
        onRequestSuccess?.(...args)
      },
    },
  )

  useImperativeHandle(
    tableRef,
    () => ({
      search(pageInfo = {}) {
        const { pageSize: size, current } = pageInfo
        return requestMaterial.run({
          // current: current || requestMaterial?.pagination?.current || 1,
          current: 1,
          pageSize: size || requestMaterial?.pagination?.pageSize || 10,
        })
      },
      reset(values) {
        form.reset(values || defaultValues || ({} as any))
        return requestMaterial.run({
          current: 1,
          pageSize: requestMaterial?.pagination?.pageSize,
        })
      },
      getRealTimeParams() {
        const values = form.getValues() || {}
        const params = {
          ...(paramsAdapter?.(values as any) || values),
          page: lastRequestParams.current?.page,
          num: lastRequestParams.current?.num,
        }
        return params
      },
      getLatestRequestParams() {
        return lastRequestParams.current
      },
      mutate(args: any) {
        const mutate = requestMaterial.fetches?.[AHOOKS_USE_REQUEST_DEFAULT_KEY]?.mutate
        return mutate?.(args)
      },
      form: form as any,
    }),
    [form, lastRequestParams, requestMaterial],
  )

  useEffect(() => {
    if (typeof request === 'function') {
      tableRef.current?.search({ current: 1, pageSize: pageSize || 10 })
      return
    }
    if (!request?.dep) return
    const { dep } = request
    let canFetch = true
    dep?.forEach((d) => {
      if (!d) canFetch = false
    })
    if (!canFetch) return
    tableRef.current?.search({ current: 1, pageSize: pageSize || 10 })
  }, [request])

  if (!paginated) {
    return {
      form,
      tableRef,
      ...requestMaterial,
      // @ts-ignore
      tableProps: {
        dataSource: (requestMaterial.data || []) as Result[],
        pagination: false as any,
      },
    }
  }

  return {
    ...requestMaterial,
    tableProps: {
      ...requestMaterial.tableProps,
      pagination: {
        ...requestMaterial.pagination,
        ...requestMaterial.tableProps.pagination,
        // hideOnSinglePage: true,
      } as any,
    },
    form,
    tableRef,
  }
}

export default useTableSearch
